import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../app/hooks';
import {
  selectFeatureFlagHitl3,
  selectFeatureFlagHitlExpressTimer,
  selectHitlExpressTimerMaxSeconds,
} from '../redux/features/config/config.selector';
import {
  selectIsExpressTimerElapsed,
  selectTaskAssignedSessionStartedAt,
} from '../redux/features/taskRouter/taskRouter.selector';
import { setExpressTimerElapsed } from '../redux/features/taskRouter/taskRouter.slice';
import { selectIsCarExited } from '../redux/features/sessionBoundary/sessionBoundary.selectors';
import logger from '../utils/logger';

/**
 * HITL-1359 (Phase 2): when the order's elapsed time (measured from the
 * task_assigned `session_started_at`) crosses the `hitl_express_timer`
 * `max_seconds` limit, latch `isExpressTimerElapsed` so the cart logic enters
 * express mode and suppresses `do_not_upsell_in_express_mode`-tagged upsells
 * for the rest of the order.
 *
 * Gated by hitl_3_0 + hitl_express_timer. Mounted in MainPageLayout (new UX),
 * so hitl_3_0 is already implied; the flag/maxSeconds guards keep it inert
 * otherwise. Uses a single setTimeout for the exact remaining time rather than
 * polling — the latch only needs to flip once. The reset is handled by the
 * taskRouter `assigned` reducer on the next task assignment.
 */
const useExpressTimer = () => {
  const dispatch = useAppDispatch();
  const isHitl3 = useSelector(selectFeatureFlagHitl3);
  const isEnabled = useSelector(selectFeatureFlagHitlExpressTimer);
  const maxSeconds = useSelector(selectHitlExpressTimerMaxSeconds);
  const sessionStartedAt = useSelector(selectTaskAssignedSessionStartedAt);
  const alreadyElapsed = useSelector(selectIsExpressTimerElapsed);
  // Car exit ends the order — drop the express-timer latch (and its header bolt)
  // the moment the car leaves rather than waiting for the next task assignment.
  const carExited = useSelector(selectIsCarExited);

  // Drop the express-timer latch (and its header bolt) on car exit rather than
  // waiting for the next task assignment to clear it.
  useEffect(() => {
    if (carExited && alreadyElapsed) {
      logger.debug('[EXPRESS_TIMER] car exit — resetting express mode');
      dispatch(setExpressTimerElapsed(false));
    }
  }, [carExited, alreadyElapsed, dispatch]);

  useEffect(() => {
    if (
      !isHitl3 ||
      !isEnabled ||
      maxSeconds <= 0 ||
      !sessionStartedAt ||
      alreadyElapsed ||
      // Car exited: the order is ending and the stale session_started_at is not
      // cleared until the next assignment. Without this guard, re-latching here
      // fights the car-exit reset effect above in an infinite render loop.
      carExited
    ) {
      return;
    }

    const start = new Date(sessionStartedAt).getTime();
    if (isNaN(start)) {
      return;
    }

    const remainingMs = maxSeconds * 1000 - (new Date().getTime() - start);
    if (remainingMs <= 0) {
      logger.debug(
        `[EXPRESS_TIMER] elapsed on assignment (started ${sessionStartedAt}, limit ${maxSeconds}s) — entering express mode`
      );
      dispatch(setExpressTimerElapsed(true));
      return;
    }

    const timer = setTimeout(() => {
      logger.debug(
        `[EXPRESS_TIMER] limit ${maxSeconds}s reached (started ${sessionStartedAt}) — entering express mode`
      );
      dispatch(setExpressTimerElapsed(true));
    }, remainingMs);

    return () => {
      clearTimeout(timer);
    };
  }, [
    isHitl3,
    isEnabled,
    maxSeconds,
    sessionStartedAt,
    alreadyElapsed,
    carExited,
    dispatch,
  ]);
};

export default useExpressTimer;
